/**
 * Déroulement d'une mission : scénario choisi à quai, vagues, intermissions, ressources, boss, fin de mission.
 * Port de AWaveDirector et de la partie session de ASpaceArenaGameMode, dans un monde persistant :
 * hors mission, le joueur vole librement ; une mission démarre au décollage et se termine sans arrêter le jeu.
 */
import type { EnemyRole, ScenarioData, SectorId } from '../../data';
import { Quat, Vec3, UP } from '../core/math';
import { Ship, type Pickup } from '../entities';
import { aiMotorProfile } from '../flight/motor';
import type { DamageInfo, SimState } from '../state';
import { Telemetry } from '../telemetry/telemetry';

/**
 * Mode de départ d'une simulation : « sandbox » = monde sans mission ; un identifiant de scénario
 * (« mission », « bossPractice », « siege »…) = scénario lancé dès le départ (tests, pilote automatique, captures).
 */
export type MissionMode = string;
/** `objective` : vagues terminées, il reste les tourelles à détruire. */
export type MissionPhase = 'idle' | 'briefed' | 'starting' | 'wave' | 'intermission' | 'boss' | 'objective' | 'victory' | 'defeat' | 'abandoned';
export type MissionOutcome = 'victory' | 'defeat' | 'abandoned';

export interface SpawnRequest {
  role: EnemyRole | 'boss';
  position: Vec3;
  rotation: Quat;
  /** Vaisseau visé à la place du joueur (escorte). */
  target?: number;
}

export class Mission {
  phase: MissionPhase = 'idle';
  scenarioId: string | null = null;
  scenario: ScenarioData | null = null;
  /** Indice de la vague courante dans le scénario (−1 avant la première). */
  waveIndex = -1;
  /** Temps restant avant la prochaine rencontre. */
  timer = 0;
  readonly tracked = new Set<number>();
  bossId: number | null = null;
  /** Tourelles encore debout (objectif « turrets ») et leur nombre initial. */
  readonly turrets = new Set<number>();
  turretTotal = 0;
  /** Escorte : convoi, tronçon courant de sa route, arrivée. */
  convoyId: number | null = null;
  convoyLeg = 0;
  convoyArrived = false;
  startTime: number | null = null;
  endTime: number | null = null;

  constructor(
    private readonly state: SimState,
    private readonly spawn: (request: SpawnRequest) => Ship,
  ) {
    state.onKill((ship, info) => this.onKill(ship, info));
  }

  get totalWaves(): number { return this.scenario?.waves.length ?? 0; }
  get finished(): boolean { return this.phase === 'victory' || this.phase === 'defeat' || this.phase === 'abandoned'; }
  /** Mission en cours (entre le décollage et sa fin). */
  get active(): boolean { return this.phase === 'starting' || this.phase === 'wave' || this.phase === 'intermission' || this.phase === 'boss' || this.phase === 'objective'; }
  /** Il reste des tourelles à détruire, ou le convoi n'est pas encore arrivé. */
  get objectivePending(): boolean {
    if (this.scenario?.objective === 'turrets') return this.turrets.size > 0;
    if (this.scenario?.objective === 'escort') return !this.convoyArrived && !!this.convoy;
    return false;
  }

  /** Convoi escorté, s'il est en vie. */
  get convoy(): Ship | null {
    const ship = this.state.findShip(this.convoyId);
    return ship?.alive ? ship : null;
  }

  /** Avancement du convoi sur sa route (0 → 1). */
  convoyProgress(): number {
    const route = this.state.zoneData?.convoy;
    const ship = this.convoy;
    if (!route || !ship) return this.convoyArrived ? 1 : 0;
    const legs = route.path.slice(1).map((p, i) => Vec3.from(p).distanceTo(Vec3.from(route.path[i]!)));
    const total = legs.reduce((a, b) => a + b, 0);
    let done = legs.slice(0, this.convoyLeg).reduce((a, b) => a + b, 0);
    done += Vec3.from(route.path[this.convoyLeg]!).distanceTo(ship.position);
    return Math.min(1, done / total);
  }
  get enemiesRemaining(): number { return this.tracked.size; }
  get label(): string { return this.scenario?.label ?? ''; }

  /** Secteur d'où arrive la prochaine rencontre (affiché pendant l'intermission). */
  nextArrival(): SectorId {
    const next = this.waveIndex + 1;
    if (next >= this.totalWaves) return this.state.data.waves.bossArrival;
    return this.waveData(next).arrival;
  }

  /**
   * Choisit un scénario (à quai) ; il démarre au décollage. Refusé pendant une mission en cours (une seule
   * mission à la fois) et pour une mission d'une autre zone (il faut d'abord sauter).
   */
  prepare(id: string): boolean {
    const scenario = this.state.data.missions.scenarios[id];
    if (!scenario || this.active || scenario.zone !== this.state.zone) return false;
    this.scenarioId = id;
    this.scenario = scenario;
    this.phase = 'briefed';
    this.state.emit({ type: 'missionBriefed', time: this.state.time, scenario: id });
    return true;
  }

  /** Décollage avec un scénario choisi : la mission commence (score, télémétrie et réserves remis à zéro). */
  start(): void {
    if (this.phase !== 'briefed') return;
    const state = this.state;
    this.phase = 'starting';
    this.timer = state.data.waves.firstWaveDelay;
    this.waveIndex = -1;
    this.tracked.clear();
    this.bossId = null;
    this.turrets.clear();
    this.turretTotal = 0;
    this.convoyId = null;
    this.convoyLeg = 0;
    this.convoyArrived = false;
    this.startTime = state.time;
    this.endTime = null;
    state.telemetry = new Telemetry(state.time);
    Object.assign(state.score, { total: 0, multiplier: 1, streakTimer: 0, bestMultiplier: 1 });
    state.control.reserves = state.data.carrier.respawn.reserves;
    state.emit({ type: 'missionStarted', time: state.time, scenario: this.scenarioId! });
    if (this.scenario?.objective === 'turrets') this.deployTurrets();
    if (this.scenario?.objective === 'escort') this.launchConvoy();
  }

  /** Convoi allié au départ de sa route, cap sur le point suivant. */
  private launchConvoy(): void {
    const state = this.state;
    const route = state.zoneData?.convoy;
    if (!route) return;
    const start = Vec3.from(route.path[0]!), next = Vec3.from(route.path[1]!);
    const motor = aiMotorProfile({ maxSpeed: route.speed, acceleration: route.speed, turnRateDeg: 10, releaseDeceleration: 0, brakeDeceleration: 0, turningBoost: 0 });
    const ship = new Ship(state.newId(), 'convoy', 'player', start, Quat.lookRotation(next.sub(start).normalized(), UP()), route.health, route.radius, motor);
    state.ships.push(ship);
    this.convoyId = ship.id;
    state.emit({ type: 'convoy', time: state.time, phase: 'launched', shipId: ship.id, position: start.toArray() });
  }

  /** Le convoi suit sa route à vitesse constante ; arrivé au bout, la mission est gagnée. */
  private moveConvoy(dt: number): void {
    const route = this.state.zoneData?.convoy;
    const ship = this.convoy;
    if (!route || !ship || this.convoyArrived) return;
    let step = route.speed * dt;
    while (step > 0 && this.convoyLeg < route.path.length - 1) {
      const target = Vec3.from(route.path[this.convoyLeg + 1]!);
      const to = target.sub(ship.position);
      const d = to.length();
      if (d <= step) {
        ship.position.copy(target);
        step -= d;
        this.convoyLeg++;
        continue;
      }
      const dir = to.scale(1 / d);
      ship.position.addScaledInPlace(dir, step);
      ship.velocity.copy(dir.scale(route.speed));
      ship.rotation = Quat.lookRotation(dir, UP());
      step = 0;
    }
    if (this.convoyLeg >= route.path.length - 1) {
      this.convoyArrived = true;
      this.state.emit({ type: 'convoy', time: this.state.time, phase: 'arrived', shipId: ship.id, position: ship.position.toArray() });
      this.end('victory');
    }
  }

  /** Tourelles de la forteresse de la zone, armées dès le début de la mission. */
  private deployTurrets(): void {
    const fortress = this.state.fortress;
    if (!fortress) return;
    fortress.data.turrets.forEach((_, i) => {
      const pose = fortress.turretPose(i);
      this.turrets.add(this.spawn({ role: 'turret', position: pose.position, rotation: pose.rotation }).id);
    });
    this.turretTotal = this.turrets.size;
    this.state.emit({ type: 'turretsDeployed', time: this.state.time, count: this.turretTotal });
  }

  update(dt: number): void {
    if (this.active) this.moveConvoy(dt);
    if (this.phase === 'starting' || this.phase === 'intermission') {
      this.timer -= dt;
      if (this.timer <= 0) this.advance();
    }
  }

  /** Plus d'appareil en réserve : la mission est perdue. */
  fail(): void {
    if (this.active) this.end('defeat');
  }

  /** Abandon depuis un dock ; un scénario choisi mais pas lancé est simplement annulé. */
  abandon(): void {
    if (this.active) this.end('abandoned');
    else if (this.phase === 'briefed') this.reset();
  }

  /** Écourte l'intermission (touche N). */
  skipIntermission(): boolean {
    if (this.phase !== 'intermission' || !this.state.player.alive) return false;
    this.timer = 0;
    this.advance();
    return true;
  }

  private reset(): void {
    this.phase = 'idle';
    this.scenarioId = null;
    this.scenario = null;
  }

  private waveData(index: number) {
    const id = this.scenario!.waves[index]!;
    const wave = this.state.data.waves.waves.find((w) => w.id === id);
    if (!wave) throw new Error(`Vague inconnue dans le scénario ${this.scenarioId} : ${id}`);
    return wave;
  }

  private advance(): void {
    const next = this.waveIndex + 1;
    if (next < this.totalWaves) this.startWave(next);
    else if (this.scenario?.boss) this.spawnBoss();
    else if (this.objectivePending) this.phase = 'objective';
    else this.end('victory');
  }

  private startWave(index: number): void {
    const state = this.state;
    const waves = state.data.waves;
    const wave = this.waveData(index);
    this.waveIndex = index;
    this.phase = 'wave';
    // Escorte : les vagues arrivent autour du convoi.
    const player = (this.convoy ?? state.player).position;
    const anchor = sectorCenter(state, wave.arrival);
    const route = anchor.sub(player).normalized(new Vec3(0, 0, -1));
    const routeRight = route.cross(UP()).normalized(new Vec3(1, 0, 0));
    const count = wave.composition.length;
    wave.composition.forEach((role, i) => {
      const spread = count > 1 ? -0.5 + i / (count - 1) : 0;
      const desired = player
        .add(route.scale(wave.formationRadius))
        .add(routeRight.scale(spread * wave.formationRadius * waves.formationLateral))
        .add(UP().scale(spread * wave.formationRadius * waves.formationVertical));
      // Les essaims de drones arrivent en nuage plutôt qu'en ligne.
      const jitter = role === 'drone'
        ? new Vec3(state.rng.range(-1, 1), state.rng.range(-1, 1), state.rng.range(-1, 1)).scale(waves.droneJitter)
        : new Vec3();
      const position = resolveSpawn(state, clampBox(desired.add(jitter), state.data.arena.spawnLimit), player, waves.spawnClearance);
      // Escorte : lourds, drones et un tireur sur deux visent le convoi.
      const convoy = this.convoy;
      const onConvoy = !!convoy && (role === 'heavy' || role === 'drone' || (role === 'marksman' && i % 2 === 0));
      const ship = this.spawn({ role, position, rotation: Quat.lookRotation(route.neg(), UP()), target: onConvoy ? convoy.id : undefined });
      this.tracked.add(ship.id);
    });
    state.emit({ type: 'waveStarted', time: state.time, wave: index + 1, total: this.totalWaves, arrival: wave.arrival });
  }

  private spawnBoss(): void {
    const state = this.state;
    const waves = state.data.waves;
    const player = state.player.position;
    const route = sectorCenter(state, waves.bossArrival).sub(player).normalized(new Vec3(-0.5, 0.15, -1).normalized());
    const offset = route.scale(waves.bossSpawnDistance).add(UP().scale(waves.bossSpawnLift));
    const desired = clampBox(player.add(offset), state.data.arena.spawnLimit);
    const position = resolveSpawn(state, desired, player, waves.bossSpawnClearance);
    const ship = this.spawn({ role: 'boss', position, rotation: Quat.lookRotation(offset.neg(), UP()) });
    this.bossId = ship.id;
    this.tracked.add(ship.id);
    this.phase = 'boss';
    this.waveIndex = this.totalWaves - 1;
    state.emit({ type: 'bossSpawned', time: state.time, shipId: ship.id });
    state.emit({ type: 'bossPhase', time: state.time, phase: 'opening' });
  }

  private onKill(ship: Ship, info: DamageInfo): void {
    const state = this.state;
    // Module de missiles : ennemis abattus par le joueur, hors drones (trop nombreux) et boss.
    if (ship.team === 'enemy' && ship.kind !== 'boss' && ship.kind !== 'drone' && info.source === 'player') {
      spawnPickup(state, 'combat', ship.position.clone());
    }
    if (ship.kind === 'player') return;
    if (ship.id === this.convoyId) {
      state.emit({ type: 'convoy', time: state.time, phase: 'lost', shipId: ship.id, position: ship.position.toArray() });
      if (this.active) this.end('defeat');
      return;
    }
    if (this.turrets.delete(ship.id)) {
      state.emit({ type: 'turretDestroyed', time: state.time, shipId: ship.id, remaining: this.turrets.size, total: this.turretTotal });
      if (this.turrets.size === 0 && this.active) this.end('victory');
      return;
    }
    if (!this.tracked.delete(ship.id)) return;
    if (ship.id === this.bossId) {
      this.end('victory');
      return;
    }
    if (this.phase === 'wave' && this.tracked.size === 0) this.waveCleared();
  }

  private waveCleared(): void {
    const state = this.state;
    const waves = state.data.waves;
    state.emit({ type: 'waveCleared', time: state.time, wave: this.waveIndex + 1 });
    if (this.waveIndex + 1 >= this.totalWaves && !this.scenario?.boss) {
      // Plus de renforts : il reste éventuellement les tourelles.
      if (this.objectivePending) this.phase = 'objective';
      else this.end('victory');
      return;
    }
    const repaired = repair(state.player, waves.repairPerWave);
    if (repaired > 0) state.emit({ type: 'repair', time: state.time, amount: repaired });
    // Module du Puits Ambre (WaveDirector::SpawnIntermissionResource).
    const res = state.data.arena.amberResource;
    const offset = (Math.max(0, this.waveIndex % 3) - 1) * res.offsetStep;
    const well = sectorCenter(state, 'amberWell');
    // Unreal : (Offset, −Offset × 0,4, 14 m) en X avant / Y droite / Z haut.
    spawnPickup(state, 'arena', well.add(new Vec3(offset * res.lateralRatio, res.lift, -offset)));
    this.phase = 'intermission';
    this.timer = waves.intermission;
  }

  /** Fin de mission : les ennemis encore présents se replient (sortie en saut), les réserves redeviennent illimitées. */
  private end(outcome: MissionOutcome): void {
    if (this.finished) return;
    const state = this.state;
    this.phase = outcome;
    this.endTime = state.time;
    state.emit({ type: 'missionEnded', time: state.time, outcome });
    for (const ship of state.ships) {
      if (!ship.alive || ship.team !== 'enemy') continue;
      ship.alive = false;
      // Les tourelles restantes se désactivent (pas de saut) ; elles reviennent à la prochaine mission.
      if (ship.kind !== 'turret') state.emit({ type: 'enemyWithdrew', time: state.time, shipId: ship.id, position: ship.position.toArray() });
    }
    state.spawnQueue.length = 0;
    this.tracked.clear();
    this.turrets.clear();
    // Le convoi quitte la zone (arrivé : il entame sa descente) ; il ne reste pas dans le monde.
    const convoyIndex = state.ships.findIndex((s) => s.id === this.convoyId);
    if (convoyIndex >= 0) state.ships.splice(convoyIndex, 1);
    state.control.reserves = Infinity;
  }
}

function repair(ship: Ship, amount: number): number {
  if (!ship.alive || amount <= 0) return 0;
  const restored = Math.min(amount, ship.maxHealth - ship.health);
  ship.health += restored;
  return restored;
}

export function sectorCenter(state: SimState, id: SectorId): Vec3 {
  const sector = state.data.arena.sectors.find((s) => s.id === id);
  if (!sector) throw new Error(`Secteur inconnu : ${id}`);
  return Vec3.from(sector.center);
}

function clampBox(v: Vec3, limit: number): Vec3 {
  return new Vec3(
    Math.max(-limit, Math.min(limit, v.x)),
    Math.max(-limit, Math.min(limit, v.y)),
    Math.max(-limit, Math.min(limit, v.z)),
  );
}

/**
 * Place un apparition hors des volumes : candidat désiré, puis alternatives décalées
 * (WaveDirector::ResolveSpawnLocation, avec un test de recouvrement exact au lieu de boîtes englobantes).
 */
export function resolveSpawn(state: SimState, desired: Vec3, player: Vec3, clearance: number): Vec3 {
  const waves = state.data.waves;
  const limit = state.data.arena.spawnLimit;
  const free = (p: Vec3) => !state.collision.overlapsSphere(p, clearance);
  if (free(desired)) return desired;
  const route = desired.sub(player).normalized(new Vec3(0, 0, -1));
  const right = route.cross(UP()).normalized(new Vec3(1, 0, 0));
  const alternatives = [UP(), UP().neg(), right, right.neg(), UP().add(right).normalized(), UP().sub(right).normalized()];
  let candidate = desired;
  for (let ring = 1; ring <= 4; ring++) {
    for (let i = 0; i < alternatives.length; i++) {
      candidate = clampBox(desired.add(alternatives[i]!.scale((waves.alternativeOffset + i * waves.alternativeStep) * ring)), limit);
      if (free(candidate)) return candidate;
    }
  }
  return candidate;
}

export function spawnPickup(state: SimState, kind: Pickup['kind'], position: Vec3): Pickup {
  const cfg = state.data.weapons.pickup;
  const pickup: Pickup = {
    id: state.newId(),
    kind,
    position,
    life: kind === 'arena' ? cfg.arenaLife : cfg.combatLife,
    amount: cfg.amount,
    alive: true,
    attracted: false,
  };
  state.pickups.push(pickup);
  state.emit({ type: 'pickupSpawned', time: state.time, pickupId: pickup.id, kind, position: position.toArray() });
  return pickup;
}

/** Attraction et collecte des modules (MissilePickup.cpp). Un stock plein ne détruit pas le module. */
export function updatePickups(state: SimState, dt: number): void {
  const cfg = state.data.weapons.pickup;
  const player = state.player;
  const w = state.weapons;
  for (const pickup of state.pickups) {
    if (!pickup.alive) continue;
    pickup.life -= dt;
    if (pickup.life <= 0) {
      pickup.alive = false;
      state.emit({ type: 'pickupExpired', time: state.time, pickupId: pickup.id });
      continue;
    }
    if (!player.alive) continue;
    const notFull = w.missiles < w.maxMissiles;
    const to = player.position.sub(pickup.position);
    const distance = to.length();
    pickup.attracted = notFull && distance <= cfg.attractRadius && state.collision.lineOfSight(pickup.position, player.position);
    if (pickup.attracted && distance > 1e-6) {
      pickup.position.addScaledInPlace(to.scale(1 / distance), Math.min(distance, cfg.attractSpeed * dt));
    }
    if (notFull && pickup.position.distanceTo(player.position) <= cfg.collectRadius) {
      const before = w.missiles;
      w.missiles = Math.min(w.maxMissiles, w.missiles + pickup.amount);
      pickup.alive = false;
      state.emit({ type: 'pickupCollected', time: state.time, pickupId: pickup.id, gained: w.missiles - before });
    }
  }
  for (let i = state.pickups.length - 1; i >= 0; i--) {
    if (!state.pickups[i]!.alive) state.pickups.splice(i, 1);
  }
}
